import React, { useEffect, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { formatMoney, today } from '../../utils/format';
import PageHeader from '../../components/common/PageHeader';
import Loader from '../../components/common/Loader';
import { showError } from '../../utils/alert';

const unwrap = (rows) => (rows && Array.isArray(rows.data) ? rows.data : rows || []);

const rowBlock = (title, rows, total) => (
  <div className="card" style={{ marginBottom: 18 }}>
    <div className="card-header"><div className="card-title">{title}</div></div>
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr><th>Account</th><th className="table-num">Amount</th></tr>
        </thead>
        <tbody>
          {unwrap(rows).map((r) => (
            <tr key={r.account_id || r.id || r.name}>
              <td>{r.name}</td>
              <td className="table-num">{formatMoney(r.amount || r.total)}</td>
            </tr>
          ))}
          {(!rows || rows.length === 0) && (
            <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No entries</td></tr>
          )}
          <tr style={{ background: '#f8fafc' }}>
            <td style={{ fontWeight: 700 }}>Total</td>
            <td className="table-num" style={{ fontWeight: 700 }}>{formatMoney(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

export default function BalanceSheet() {
  const [asOf, setAsOf] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.reports.balanceSheet({ as_of: asOf });
      setData(res);
    } catch (err) {
      showError('Failed to load balance sheet', extractError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <PageHeader
        title="Balance Sheet"
        subtitle="Financial position as of a given date."
        crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Balance Sheet' }]}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <input className="form-control" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            <button className="btn btn-primary btn-sm" onClick={load}><FiRefreshCw /> Run</button>
          </div>
          {loading ? <Loader text="Loading balance sheet..." /> : null}

          {data && (
            <>
              <div className="stats-grid" style={{ marginBottom: 18 }}>
                <div className="stat-card"><div className="stat-icon green"><i className="bi bi-bank" /></div><div><div className="stat-value">{formatMoney(data.assets?.total)}</div><div className="stat-label">Total Assets</div></div></div>
                <div className="stat-card"><div className="stat-icon red"><i className="bi bi-file-earmark-text" /></div><div><div className="stat-value">{formatMoney(data.liabilities?.total)}</div><div className="stat-label">Total Liabilities</div></div></div>
                <div className="stat-card"><div className="stat-icon indigo"><i className="bi bi-bar-chart-fill" /></div><div><div className="stat-value">{formatMoney(data.equity?.total)}</div><div className="stat-label">Total Equity</div></div></div>
                <div className="stat-card">
                  <div className={`stat-icon ${data.balanced ? 'green' : 'red'}`}>{data.balanced ? <i className="bi bi-check-circle-fill" /> : <i className="bi bi-exclamation-triangle-fill" />}</div>
                  <div><div className="stat-value">{data.balanced ? 'Balanced' : 'Not Balanced'}</div><div className="stat-label">A = L + E</div></div>
                </div>
              </div>
              {rowBlock('Assets', data.assets?.rows, data.assets?.total)}
              {rowBlock('Liabilities', data.liabilities?.rows, data.liabilities?.total)}
              {rowBlock('Equity', data.equity?.rows, data.equity?.total)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}